import { FilterConfigToggleEntry } from '@alltrails/shared/types/filters';
import { PageStrings } from '@alltrails/shared/utils/constants/pageStringHelpers';
import { StringConstantsMixin } from 'react_components/mixins/shared/string_constants_mixin';
import { Context } from '@alltrails/shared/types/context';
import { FilterConfig, FilterConfigRange, FilterConfigSlider, FiltersKey, LinkedStatus } from 'types/Search';
import { Sort } from 'types/Search/Sort';
import { Access, Activities, CompletedStatus, Features } from 'types/Trails';
import { convertIndexToValue, convertValueToIndex, formattedStepForIndex } from 'utils/slider';

export type FilterConfigConstructorArgs = {
  context?: Context;
  initialUserSlug?: string;
  page?: string;
};

type FilterConfigConstructor = ({ context, initialUserSlug, page }: FilterConfigConstructorArgs) => FilterConfig;

function valueToStringKey(value: string) {
  return value.toUpperCase().replace(/-/g, '_');
}

function newAccessFilterConfig() {
  const access = {} as Record<Access, FilterConfigToggleEntry>;

  [Access.DOGS, Access.KIDS, Access.ADA, Access.STROLLERS, Access.PAVED, Access.PARTIALLY_PAVED].forEach(value => {
    const stringKey = valueToStringKey(value);

    access[value] = {
      value: value === Access.DOGS ? [Access.DOGS, Access.DOGS_LEASH] : value,
      name: (StringConstantsMixin.strings.FILTERS.ACCESS as Record<string, string>)[stringKey],
      selected: false
    };
  });

  return access;
}

function newActivitiesFilterConfig() {
  const activities = {} as Record<Activities, FilterConfigToggleEntry>;

  // This order comes from DISCO-8 and https://docs.google.com/spreadsheets/d/1imyOWwQA1_2wcnyZnD4AwmPjOUmjxa40gllCr_1XtQs/edit#gid=15327538
  [
    Activities.HIKING,
    Activities.MOUNTAIN_BIKING,
    Activities.TRAIL_RUNNING,
    Activities.ROAD_BIKING,
    Activities.BACKPACKING,
    Activities.WALKING,
    Activities.OFF_ROAD_DRIVING,
    Activities.SCENIC_DRIVING,
    Activities.BIKE_TOURING,
    Activities.SNOWSHOEING,
    Activities.CROSS_COUNTRY_SKIING,
    Activities.SKIING,
    Activities.PADDLE_SPORTS,
    Activities.CAMPING,
    Activities.FISHING,
    Activities.BIRDING,
    Activities.HORSEBACK_RIDING,
    Activities.ROCK_CLIMBING,
    Activities.VIA_FERRATA
  ].forEach(value => {
    const stringKey = valueToStringKey(value);

    activities[value] = {
      value,
      name: (StringConstantsMixin.strings.FILTERS.ACTIVITIES as Record<string, string>)[stringKey],
      selected: false
    };
  });

  return activities;
}

function newClosedStatusFilterConfig() {
  return {
    open: {
      value: false,
      name: StringConstantsMixin.strings.FILTERS.OPEN,
      selected: false
    },
    closed: {
      value: true,
      name: StringConstantsMixin.strings.FILTERS.OBSTACLES.CLOSED,
      selected: false
    }
  };
}

function newCompletedStatusFilterConfig() {
  const completed = {} as Record<CompletedStatus, FilterConfigToggleEntry>;

  ['uncompleted', 'unverified', 'verified'].forEach((value: CompletedStatus) => {
    const stringKey = valueToStringKey(value);

    completed[value] = {
      value,
      name: (StringConstantsMixin.strings.FILTERS as Record<string, any>)[stringKey],
      selected: false
    };
  });

  return completed;
}

function newDifficultyFilterConfig() {
  return {
    easy: {
      value: [1, 2],
      name: StringConstantsMixin.strings.DIFFICULTY.NORMAL.EASY,
      selected: false
    },
    moderate: {
      value: [3, 4],
      name: StringConstantsMixin.strings.DIFFICULTY.NORMAL.MODERATE,
      selected: false
    },
    hard: {
      value: [5, 6, 7, 8],
      name: StringConstantsMixin.strings.DIFFICULTY.NORMAL.HARD,
      selected: false
    }
  };
}

function newElevationGainFilterConfig({ context }: FilterConfigConstructorArgs) {
  if (context.displayMetric) {
    return {
      stepIncreaseAt: [0, 10],
      stepIncreaseBy: [30, 300],
      upperBound: 14,
      displayFunction: 'm',
      multiplier: 1,
      value: [0, -1],
      displayValue: [0, 14],
      formattedRange: ['0', '1,500+']
    };
  }

  return {
    stepIncreaseAt: [0, 10],
    stepIncreaseBy: [100, 1000],
    upperBound: 14,
    displayFunction: 'ft',
    multiplier: 0.3048,
    value: [0, -1],
    displayValue: [0, 14],
    formattedRange: ['0', '5,000+']
  };
}

function newEmptyArrayFilterConfig(): [] {
  return [];
}

function newEmptyObjectFilterConfig() {
  return {};
}

function newFalseFilterConfig() {
  return false;
}

function newInitialSlugFilterConfig({ initialUserSlug }: FilterConfigConstructorArgs) {
  return initialUserSlug;
}

function newFeaturesFilterConfig() {
  const features = {} as Record<Features, FilterConfigToggleEntry>;

  [
    Features.WATERFALL,
    Features.VIEWS,
    Features.LAKE,
    Features.RIVER,
    Features.FOREST,
    Features.HOT_SPRINGS,
    Features.BEACH,
    Features.WILD_FLOWERS,
    Features.WILDLIFE,
    Features.CAVE,
    Features.HISTORIC_SITE,
    Features.RAILS_TRAILS,
    Features.CITY_WALK,
    Features.PUB_CRAWL,
    Features.EVENT
  ].forEach(value => {
    const stringKey = valueToStringKey(value);

    features[value] = {
      value,
      name: (StringConstantsMixin.strings.FILTERS.FEATURE_TYPES as Record<string, string>)[stringKey],
      selected: false
    };
  });

  return features;
}

function newLengthsFilterConfig({ context }: FilterConfigConstructorArgs) {
  if (context.displayMetric) {
    return {
      // 0 - 40 step by 1, 40 - 80 step by 8
      stepIncreaseAt: [0, 40],
      stepIncreaseBy: [1, 8],
      upperBound: 45,
      displayFunction: 'km',
      multiplier: 1000.0,
      value: [0, -1],
      displayValue: [0, 45],
      formattedRange: ['0', '80+']
    };
  }

  return {
    // 0 - 25 step by 1, 25 - 50 step by 5
    stepIncreaseAt: [0, 25],
    stepIncreaseBy: [1, 5],
    upperBound: 30,
    displayFunction: 'mi',
    multiplier: 1609.344,
    value: [0, -1],
    displayValue: [0, 30],
    formattedRange: ['0', '50+']
  };
}

function newLinkedStatusFilterConfig() {
  const linked = {} as Record<LinkedStatus, FilterConfigToggleEntry>;

  ['unlinked', 'linked'].forEach((value: LinkedStatus) => {
    const stringKey = `TRAIL_${valueToStringKey(value)}`;

    linked[value] = {
      value,
      name: (StringConstantsMixin.strings.FILTERS as Record<string, any>)[stringKey],
      selected: false
    };
  });

  return linked;
}

function newNullFilterConfig(): null {
  return null;
}

function newDistanceAwayFilterConfig({ context }: FilterConfigConstructorArgs) {
  if (context.displayMetric) {
    return {
      stepIncreaseAt: [0, 2],
      stepIncreaseBy: [10, 20],
      upperBound: 7,
      displayFunction: 'km',
      multiplier: 1000.0,
      value: -1,
      displayValue: 7
    };
  }

  return {
    stepIncreaseAt: [0, 2, 3],
    stepIncreaseBy: [5, 10, 20],
    upperBound: 6,
    displayFunction: 'mi',
    multiplier: 1609.344,
    value: -1,
    displayValue: 6
  };
}

function newRouteTypeFilterConfig() {
  return {
    L: {
      value: 'L',
      name: StringConstantsMixin.strings.FILTERS.ROUTE_TYPES.LOOP,
      selected: false
    },
    O: {
      value: 'O',
      name: StringConstantsMixin.strings.FILTERS.ROUTE_TYPES.OUT_AND_BACK,
      selected: false
    },
    P: {
      value: 'P',
      name: StringConstantsMixin.strings.FILTERS.ROUTE_TYPES.POINT_TO_POINT,
      selected: false
    }
  };
}

function newSortFilterConfig({ page }: FilterConfigConstructorArgs) {
  if ([PageStrings.EXPLORE_ALL_PAGE, PageStrings.EXPLORE_COMMUNITY_CONTENT_PAGE, PageStrings.EXPLORE_TRAIL_MAP_PAGE].includes(page)) {
    return {
      [Sort.BEST_MATCH]: {
        name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.BEST_MATCH,
        value: 0,
        selected: true
      },
      [Sort.MOST_POPULAR]: {
        name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.MOST_POPULAR,
        value: 0,
        selected: false
      },
      [Sort.NEWLY_ADDED]: {
        name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.NEWLY_ADDED,
        value: 0,
        selected: false
      },
      [Sort.CLOSEST]: {
        name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.CLOSEST,
        value: 0,
        selected: false
      }
    } as Record<Sort, FilterConfigToggleEntry>;
  }

  if ([PageStrings.EXPLORE_COMPLETED_PAGE].includes(page)) {
    return {
      [Sort.LIST_ORDER]: {
        name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.LIST_ORDER,
        value: -1,
        selected: true
      }
    } as Record<Sort, FilterConfigToggleEntry>;
  }

  if ([PageStrings.EXPLORE_CUSTOM_PAGE, PageStrings.EXPLORE_FAVORITE_PAGE].includes(page)) {
    return {
      [Sort.LIST_ORDER]: {
        name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.LIST_ORDER,
        value: 1,
        selected: true
      },
      [Sort.RECENTLY_ADDED]: {
        name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.RECENTLY_ADDED,
        value: 1,
        selected: false
      },
      [Sort.NAME]: {
        name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.NAME,
        value: 1,
        selected: false
      }
    } as Record<Sort, FilterConfigToggleEntry>;
  }

  if ([PageStrings.SEO_PAGE].includes(page)) {
    return {
      [Sort.MOST_POPULAR]: {
        name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.MOST_POPULAR,
        value: 0,
        selected: true
      }
    } as Record<Sort, FilterConfigToggleEntry>;
  }

  return {
    [Sort.DATE]: {
      name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.DATE,
      value: 1,
      selected: true
    },
    [Sort.NAME]: {
      name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.NAME,
      value: 1,
      selected: false
    }
  } as Record<Sort, FilterConfigToggleEntry>;
}

function newVisitorUsageFilterConfig() {
  return {
    '1': {
      value: '1',
      name: StringConstantsMixin.strings.FILTERS.USAGES.LIGHT,
      selected: false
    },
    '2': {
      value: '2',
      name: StringConstantsMixin.strings.FILTERS.USAGES.MODERATE,
      selected: false
    },
    '3': {
      value: ['3', '4'],
      name: StringConstantsMixin.strings.FILTERS.USAGES.HEAVY,
      selected: false
    }
  };
}

function newAlertTypeFilterConfig() {
  return {
    W: {
      value: 'W',
      name: StringConstantsMixin.strings.ALERTS.TYPES.WARNING,
      selected: false
    },
    I: {
      value: 'I',
      name: StringConstantsMixin.strings.ALERTS.TYPES.INFORMATIONAL,
      selected: false
    },
    C: {
      value: 'C',
      name: StringConstantsMixin.strings.ALERTS.TYPES.CLOSURE,
      selected: false
    }
  };
}

const filterKeyToConstructor = {
  access: newAccessFilterConfig,
  alert_type: newAlertTypeFilterConfig,
  areas: newEmptyObjectFilterConfig,
  activities: newActivitiesFilterConfig,
  boundingBox: newNullFilterConfig,
  cities: newEmptyObjectFilterConfig,
  closed_status: newClosedStatusFilterConfig,
  completed: newCompletedStatusFilterConfig,
  countries: newEmptyObjectFilterConfig,
  difficulty: newDifficultyFilterConfig,
  distance_away: newDistanceAwayFilterConfig,
  elevation_gain: newElevationGainFilterConfig,
  features: newFeaturesFilterConfig,
  has_profile_photo: newNullFilterConfig,
  initialUserSlug: newInitialSlugFilterConfig,
  initLocationObject: newNullFilterConfig,
  isMinimumViableContent: newNullFilterConfig,
  lengths: newLengthsFilterConfig,
  linked: newLinkedStatusFilterConfig,
  locationObject: newNullFilterConfig,
  mapIds: newEmptyArrayFilterConfig,
  minRating: newNullFilterConfig,
  mobileMap: newFalseFilterConfig,
  queryTerm: newNullFilterConfig,
  route_type: newRouteTypeFilterConfig,
  sort: newSortFilterConfig,
  states: newEmptyObjectFilterConfig,
  trailIds: newEmptyArrayFilterConfig,
  trackIds: newEmptyArrayFilterConfig,
  alertIds: newEmptyArrayFilterConfig,
  visitor_usage: newVisitorUsageFilterConfig,
  zoomLevel: newNullFilterConfig
} as Record<FiltersKey, FilterConfigConstructor>;

function newFilterConfigForKey(key: FiltersKey, args: FilterConfigConstructorArgs): FilterConfig {
  const configConstructor = filterKeyToConstructor[key];
  if (!configConstructor) {
    console.warn('no filter config constructor for:', key);
    return null;
  }

  return configConstructor(args);
}

function applyRangeUpdates(filter: FilterConfigRange, values: [number, number]) {
  const { multiplier, upperBound, stepIncreaseAt, stepIncreaseBy } = filter;

  let parsedData = [...values];
  const maxParsedValue = parsedData[1];

  const maxPossibleValue = convertIndexToValue({
    index: filter.upperBound,
    stepIncreaseAt: filter.stepIncreaseAt,
    stepIncreaseBy: filter.stepIncreaseBy,
    multiplier: filter.multiplier
  });

  if (maxParsedValue > maxPossibleValue) {
    parsedData = [0, maxPossibleValue];
  }

  const updates = {
    displayRange: [convertValueToIndex({ value: parsedData[0], multiplier, stepIncreaseAt, stepIncreaseBy })],
    convertedRange: [parsedData[0]],
    formattedRange: []
  } as {
    displayRange: number[];
    convertedRange: number[];
    formattedRange: string[];
  };

  updates.formattedRange[0] = formattedStepForIndex({ index: updates.displayRange[0], upperBound, stepIncreaseAt, stepIncreaseBy });

  if (parsedData[1] === -1) {
    updates.displayRange.push(filter.upperBound);
    updates.convertedRange.push(-1);
    updates.formattedRange.push(formattedStepForIndex({ index: filter.upperBound, upperBound, stepIncreaseAt, stepIncreaseBy }));
  } else {
    updates.displayRange.push(convertValueToIndex({ value: parsedData[1], multiplier, stepIncreaseAt, stepIncreaseBy }));
    updates.convertedRange.push(parsedData[1]);
    updates.formattedRange.push(formattedStepForIndex({ index: updates.displayRange[1], upperBound, stepIncreaseAt, stepIncreaseBy }));
  }

  // Compare the current filter values against the updates. If they are identical
  // then no updates need to be applied. This is a tiny optimization.
  if (JSON.stringify(filter.value) === JSON.stringify(updates.convertedRange)) {
    return;
  }

  filter.value = updates.convertedRange;
  filter.displayValue = updates.displayRange;
  if (updates.formattedRange) {
    filter.formattedRange = updates.formattedRange;
  }
}

function applySliderUpdates(filter: FilterConfigSlider, value: number) {
  const { multiplier, upperBound, stepIncreaseAt, stepIncreaseBy } = filter;

  const maxPossibleValue = convertIndexToValue({
    index: filter.upperBound,
    stepIncreaseAt: filter.stepIncreaseAt,
    stepIncreaseBy: filter.stepIncreaseBy,
    multiplier: filter.multiplier
  });

  if (value > maxPossibleValue) {
    value = maxPossibleValue;
  }

  const updates = {
    displayRange: [convertValueToIndex({ value, multiplier, stepIncreaseAt, stepIncreaseBy })],
    convertedRange: [value],
    formattedRange: []
  } as {
    displayRange: number[];
    convertedRange: number[];
    formattedRange: string[];
  };

  updates.formattedRange[0] = formattedStepForIndex({ index: updates.displayRange[0], upperBound, stepIncreaseAt, stepIncreaseBy });

  if (value === -1) {
    updates.displayRange = [filter.upperBound];
    updates.convertedRange = [-1];
    updates.formattedRange.push(formattedStepForIndex({ index: filter.upperBound, upperBound, stepIncreaseAt, stepIncreaseBy }));
  } else {
    updates.displayRange.push(convertValueToIndex({ value, multiplier, stepIncreaseAt, stepIncreaseBy }));
    updates.convertedRange.push(value);
    updates.formattedRange.push(
      formattedStepForIndex({
        index: updates.displayRange[1],
        upperBound,
        stepIncreaseAt,
        stepIncreaseBy
      })
    );
  }

  // Compare the current filter value against the update. If they are identical
  // then no updates need to be applied. This is a tiny optimization.
  if (filter.value === updates.convertedRange[0]) {
    return;
  }

  [filter.value] = updates.convertedRange;
  [filter.displayValue] = updates.displayRange;
  if (updates.formattedRange) {
    [filter.formattedRange] = updates.formattedRange;
  }
}

export { applyRangeUpdates, applySliderUpdates, filterKeyToConstructor, newFilterConfigForKey, newSortFilterConfig };
